import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import {
  getNewsEventReq,
  getStakeholdingReq,
  getActivistObjectiveReq,
  getPeerGroupDataReq,
  handleClearPeerGroupCompanySelection,
  handleClearPeerGroupInvestorSelection,
  getProductMembershipsReq,
} from '../../../NewsContainer/NewsSlice';

import Page from '../../../../components/Page';
import bn from '../../../../utils/bemnames';
import NewsFilter from '../../../../components/News/Search/NewsFilter';
import ArticleBrief from '../components/ArticleBrief';
import ScrollToTop from '../../../../components/GeneralForm/ScrollToTop';
import api from '../../../../api';
import { newsFilterV2 } from '../../../../../src/config/server-config';
import { AppDispatch, RootState } from '../../../../store';
import ArticleFull from '../components/ArticleFull';

const bem = bn.create('newsNew');

const LIMIT = 10;

const SearchContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const history = useHistory();

  const query = new URLSearchParams(location.search);
  const newsId = query.get('newsID');

  const newsState = useSelector((state: RootState) => state.news);
  const companySearchOptionSelection = useSelector((state: RootState) => state.dashboard.companySearchOptionSelection);
  const investorSearchOptionsSelection = useSelector(
    (state: RootState) => state.dashboard.investorSearchOptionsSelection,
  );

  const [news, setNews] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isOpenTab, setIsOpenTab] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    dispatch(getNewsEventReq(3));
    dispatch(getStakeholdingReq(2, false));
    dispatch(getActivistObjectiveReq(1));
    dispatch(getProductMembershipsReq());

    if (query.get('q')) {
      dispatch(handleClearPeerGroupInvestorSelection(null));
      dispatch(handleClearPeerGroupCompanySelection(null));
    } else {
      const optionData = {
        comp: companySearchOptionSelection,
        inve: investorSearchOptionsSelection,
      };
      dispatch(getPeerGroupDataReq(optionData));
    }
  }, [investorSearchOptionsSelection, companySearchOptionSelection]);

  const fetchMoreNews = async (pageToFetch: number, isNewFetch = false) => {
    if (isLoading || (!hasMore && !isNewFetch)) return;
    setIsLoading(true);

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const offset = pageToFetch * LIMIT;
      const payload = {
        ActivistObjectiveGroupId: newsState.filterActiveObjectiveGroupSelection?.length
          ? newsState.filterActiveObjectiveGroupSelection
          : null,
        ActivistObjectiveTypeId: newsState.filterActiveObjectiveTypeSelection?.length
          ? newsState.filterActiveObjectiveTypeSelection
          : null,
        CompanySearchId: newsState.filterByCompanySelection,
        Event: newsState.filterEvent,
        Freesearch: newsState.filterFreeSearch,
        InvestorSearchId: newsState.filterByInvestorSelection,
        Stakeholding: newsState.filterStackHolding,
        periodEnd: newsState.filterPeriodEnd,
        periodStart: newsState.filterPeriodStart,
        product_id: newsState.filterProduct,
      };

      const response = await api.post(`${newsFilterV2}?limit=${LIMIT}&offset=${offset}`, payload);

      if (!abortController.signal.aborted) {
        if (!response || response.status < 200 || response.status >= 300) {
          throw new Error('API response not ok');
        }

        const newItems = response.data || [];

        if (isNewFetch) {
          setNews(newItems);
        } else {
          setNews((prev) => [...prev, ...newItems]);
        }

        if (newItems.length < LIMIT) {
          setHasMore(false);
        }
        setIsError(false);
      }
    } catch (error) {
      setHasMore(false);
      setIsError(true);
    } finally {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (newsId || page === 0 || !hasMore) return;
    fetchMoreNews(page, false);
  }, [page, hasMore]);

  useEffect(() => {
    if (newsId) return;

    const options = { root: null, rootMargin: '200px', threshold: 0.1 };
    const observer = new IntersectionObserver(handleObserver, options);
    const lastItem = document.querySelector('.newsblock > :last-child');

    if (lastItem) observer.observe(lastItem);

    return () => {
      if (lastItem) observer.unobserve(lastItem);
    };
  }, [news, newsId]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleObserver = (entities: IntersectionObserverEntry[]) => {
    const target = entities[0];
    if (target.isIntersecting && hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSearchBtn = () => {
    setIsOpenTab(false);
    setNews([]);
    setHasMore(true);
    setIsLoading(true);
    setIsError(false);
    setPage(0);
    if (query.get('newsID')) {
      history.replace({ pathname: location.pathname });
    }
    fetchMoreNews(0, true);
  };

  const handleIsOpenTab = (isOpen: boolean) => {
    setIsOpenTab(isOpen);
  };

  const newsIds = news.map((item) => item.newsid);
  const handleGetNextPage = () => {
    fetchMoreNews(page + 1, false);
    setPage((prev) => prev + 1);
  };

  return (
    <Page className="search-container">
      <NewsFilter isOpenTab={isOpenTab} handleSearchBtn={handleSearchBtn} onIsOpenTab={handleIsOpenTab} />
      {!isOpenTab && isLoading && <div className="loading-more">Loading...</div>}
      <div className={bem.b('row')}>
        <div className="col-xs-12 col-sm-12 col-lg-12 p-0 position-relative">
          {newsId ? (
            <ArticleFull
              newsId={newsId}
              newsIds={newsIds}
              isLoading={isLoading}
              hasMore={hasMore}
              getNextPage={handleGetNextPage}
              backLink={location.pathname}
            />
          ) : (
            <div className="newsblock">
              {news.map((newsItem, index) => (
                <div key={`${newsItem.newsid}-${index}`}>
                  <ArticleBrief newsDetails={newsItem} />
                </div>
              ))}

              {isError && <div>Failed to load news. Please try again.</div>}
              {!hasMore && news.length === 0 && !isLoading && <div>No Search Results Found</div>}
              {hasMore && news.length > 0 && <div className="loading-more m-2">Loading... </div>}
            </div>
          )}
        </div>
        <ScrollToTop />
      </div>
    </Page>
  );
};

export default SearchContainer;
